import { Badge } from './badge.tsx'
import { Cabin, CabinTypeTranslation, Discount } from '../data.ts'
import { useTranslation } from 'react-i18next'
import { CabinBedIcon, OnePersonCabinIcon, ThreePersonCabinIcon, TwoPersonCabinIcon } from '../icons/cabin.tsx'
import { CabinSelection } from '../types/cabin-selection.ts'
import { useMemo } from 'react'
import { TrashIcon } from '../icons/trash.tsx'
import { MinusIcon, PlusIcon } from '../icons/plus-minus.tsx'
import { DEFAULT_LANGUAGE } from '../i18n.ts'
import { Dialog, DialogTrigger } from './ui/dialog.tsx'
import { DialogTranslationContent } from './dialog.tsx'
import { cn } from '../lib/cn.ts'
import DefaultCabinImage from '../assets/cabin-icon.svg?inline'
import { baseCabinPrice } from '@/lib/departures.ts'
import { discountedCabinPrice } from '../lib/discount.ts'
import { InformationIcon } from '@/icons/information.tsx'

const AMOUNT_DANGER = 1
const AMOUNT_WARNING = 3

export function CabinBlock ({
  discount,
  cabin,
  cabinSelection,
  addCabin,
  removeCabin,
  setCabinAmount,
  cabinTypeTranslations
}: {
  discount?: Discount | null
  cabin: Cabin
  cabinSelection: CabinSelection
  addCabin: (cabin: Cabin) => void
  removeCabin: (cabinId: string) => void
  setCabinAmount: (id: string, amount: number) => void
  cabinTypeTranslations: CabinTypeTranslation[]
}): JSX.Element {
  const { i18n, t } = useTranslation('widget')
  const amountLeft = cabin.available_count + cabin.on_request_count

  const translation = useMemo(() => {
    const matches = cabinTypeTranslations.filter(
      translation => translation.parent_id === cabin.cabin_type_base_id
    )
    return matches.find(translation => translation.language === i18n.language) ?? matches.find(translation => translation.language === DEFAULT_LANGUAGE)
  }, [cabin.cabin_type_base_id, cabinTypeTranslations, i18n.language])

  const selectedCabins = useMemo(() => cabinSelection.filter(selection => selection.cabinTypeId === cabin.cabin_type_id), [cabinSelection, cabin.cabin_type_id])

  const basePrice = useMemo(() => baseCabinPrice(cabin), [cabin])

  const displayPrice = useMemo(() => discountedCabinPrice(basePrice, discount), [basePrice, discount])

  return (
    <div
      className={cn('grid grid-cols-subgrid @desktop:col-span-3 rounded-sm bg-brand-primary-lighter', {
        'ring-2 ring-offset-2 ring-brand-secondary': selectedCabins.length > 0
      })}
    >
      <Dialog>
        <figure
          className='relative grid h-28 @desktop:h-28 @desktop:w-60 bg-gray-200 rounded-t-sm @desktop:rounded-tr-none @desktop:rounded-l-sm'
        >
          <img
            src={translation?.image_urls?.[0] ?? DefaultCabinImage}
            alt=''
            className={cn('object-cover h-28 w-full @desktop:h-28 @desktop:w-60 rounded-t-sm @desktop:rounded-tr-none', {
              '@desktop:rounded-l-sm': selectedCabins.length === 0
            })}
          />
          {translation != null && (
            <DialogTrigger
              className='absolute left-2 bottom-2 border-2 border-brand-primary bg-white text-brand-primary h-8 text-xs/1.25 font-bold rounded-md inline-flex items-center gap-2 px-2 cursor-pointer'
            >
              <CabinBedIcon />
              <span>{t('view_cabin', 'View details')}</span>
            </DialogTrigger>
          )}
        </figure>
        {translation != null && (
          <DialogTranslationContent translation={translation} />
        )}
      </Dialog>
      <div className='p-4 text-brand-primary @desktop:h-28'>
        <div className='mb-3'>
          <h3 className='font-bold'>{translation?.name ?? cabin.name}</h3>
          {cabin.minimum_occupancy < 2 && cabin.maximum_occupancy >= 2 && (
            <p className='text-black text-body-xs'>{t('available_for_single_use')}</p>
          )}
        </div>
        <div className='grid @desktop:grid-cols-2 gap-2 text-sm @desktop:max-w-145'>
          <div className='flex items-center gap-1'>
            {cabin.maximum_occupancy === 1
              ? <OnePersonCabinIcon />
              : (
                  cabin.maximum_occupancy === 2 ? <TwoPersonCabinIcon /> : <ThreePersonCabinIcon />
                )}
            {cabin.minimum_occupancy === cabin.maximum_occupancy
              ? t('x_person_cabin', {
                count: cabin.minimum_occupancy
              })
              : t('x_to_y_person_cabin', {
                min: cabin.minimum_occupancy,
                max: cabin.maximum_occupancy
              })}
          </div>
        </div>
      </div>
      <div className='p-4 pt-0 @desktop:pt-4 flex flex-col gap-2 @desktop:gap-4'>
        <div className='flex justify-between gap-4'>
          <Badge
            variant={amountLeft <= AMOUNT_DANGER ? 'error' : (amountLeft <= AMOUNT_WARNING ? 'warning' : 'success')}
            className='font-medium'
          >
            {t(amountLeft <= AMOUNT_WARNING ? 'only_x_left' : 'x_left', { count: amountLeft })}
          </Badge>
          <Badge className='text-base text-brand-primary font-bold'>{t('format.price_per_person', {
            value: displayPrice
          })}
          </Badge>
        </div>
        <div className='grid @desktop:flex justify-end'>
          {selectedCabins.length === 0
            ? (
                <button
                  type='button'
                  className='border border-brand-primary text-brand-primary px-4 py-1 rounded-md font-bold cursor-pointer'
                  onClick={() => addCabin(cabin)}
                >
                  {t('select_this_cabin', 'Select this cabin')}
                </button>
              )
            : (
                <button
                  type='button'
                  className='border border-brand-primary text-brand-primary px-4 py-1 rounded-md font-bold cursor-pointer flex items-center gap-2'
                  onClick={() => removeCabin(cabin.cabin_type_id)}
                >
                  <TrashIcon className='size-3' />
                  {t('remove_x_cabins', { count: selectedCabins.length })}
                </button>
              )}
        </div>
      </div>
      {selectedCabins.length > 0 && (
        <>
          {cabin.minimum_occupancy < 2 && cabin.maximum_occupancy >= 2 && (
            <div
              className='border-t border-t-brand-primary-light @desktop:col-span-3 px-4 py-3 flex gap-2 items-center text-body-sm'
            >
              <span><InformationIcon className='text-brand-primary size-3' /></span>
              <span className='grow shrink'>{t('single_use_notice')}</span>
            </div>
          )}
          <div
            className='rounded-b-sm border-t border-t-brand-primary-light @desktop:col-span-3 p-4 text-brand-primary flex flex-wrap gap-4'
          >
            {selectedCabins.map((selectedCabin, index) => (
              <div
                key={selectedCabin.id}
                className='bg-white border border-brand-primary-light inline-flex items-center gap-3 py-2 px-3 rounded-md'
              >
                <div className='flex flex-col'>
                  <h4 className='font-bold text-control-sm'>{t('cabin_x', { count: index + 1 })}</h4>
                  <small className='text-sm'>
                    {cabin.minimum_occupancy < cabin.maximum_occupancy ? t('maximum_occupancy_x', { count: cabin.maximum_occupancy }) : t('x_person_cabin', { count: cabin.maximum_occupancy })}
                  </small>
                </div>
                {cabin.minimum_occupancy < cabin.maximum_occupancy && (
                  <div className='inline-flex'>
                    <button
                      type='button'
                      className='size-8 flex justify-center items-center border border-base-3 rounded-l-md disabled:bg-gray-100 disabled:text-gray-400 cursor-pointer disabled:cursor-not-allowed'
                      disabled={selectedCabin.occupancy <= cabin.minimum_occupancy}
                      onClick={() => setCabinAmount(selectedCabin.id, selectedCabin.occupancy - 1)}
                      title={t('widget:button_title.remove_person', 'Remove a person from the cabin')}
                    >
                      <MinusIcon />
                    </button>
                    <div
                      className='size-8 flex justify-center items-center border-t border-b border-base-3 cursor-default'
                    >{selectedCabin.occupancy}
                    </div>
                    <button
                      type='button'
                      className='size-8 flex justify-center items-center border border-base-3 rounded-r-md disabled:bg-gray-100 disabled:text-gray-400 cursor-pointer disabled:cursor-not-allowed'
                      disabled={selectedCabin.occupancy >= cabin.maximum_occupancy}
                      onClick={() => setCabinAmount(selectedCabin.id, selectedCabin.occupancy + 1)}
                      title={t('widget:button_title.add_person', 'Add a person to the cabin')}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                )}
                <button
                  title={t('remove_cabin', 'Remove cabin')} type='button'
                  className='cursor-pointer'
                  onClick={() => removeCabin(selectedCabin.id)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {selectedCabins.length < amountLeft && (
              <button
                type='button' onClick={() => addCabin(cabin)}
                className='p-4 border border-brand-primary rounded-lg cursor-pointer font-bold'
              >
                {t('add_extra_cabin', 'Add extra cabin')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
